import {noop} from '@benchling/js-utils';
import {css, StyleDeclaration, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import {DataFrame} from 'data-frames/DataFrameTypes';
import {AnalyticsVisualization} from 'insights/analytics-visualizations/__generated/AnalyticsVisualization';
import {
  VISUALIZATION_TYPE_TO_FLEX_GROW,
  VISUALIZATION_TYPE_TO_NEEDS_PADDING,
} from 'insights/analytics-visualizations/AnalyticsVisualizationConstants';
import {
  VisualizationType,
  VisualizationTypeToParameters,
} from 'insights/analytics-visualizations/AnalyticsVisualizationTypes';
import getVisualizationType from 'insights/analytics-visualizations/data/getVisualizationType';
import {EXPANDED_BLOCK_HEIGHT, PANE_MARGIN, VISUALIZATION_EDITOR_HEIGHT} from 'insights/InsightsConstants';
import {ExperimentalShapashTableOptions} from 'machine-learning/handleClickShapashTableRow';
import getTestProps from 'test/lib/cypress/getTestProps';
import {RegressionAnalysis} from 'ui-library/charting/statistics/regression/RegressionTypes';
import {SuccessfulAnalysis} from 'ui-library/charting/statistics/Types';
import useHasBeenInView from 'ui-library/hooks/useHasBeenInView';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import BarChartViewer from './BarChartViewer';
import HeatmapViewer from './HeatmapViewer';
import LineChartViewer from './LineChartViewer';
import PieChartViewer from './PieChartViewer';
import ScatterPlotViewer from './ScatterPlotViewer';
import TableViewer from './TableViewer';
import useDelayedRender from './useDelayedRender';

export enum VisualizationHeight {
  DYNAMIC = 'DYNAMIC',
  EDITOR_HEIGHT = 'EDITOR_HEIGHT',
  EXPANDED_EDITOR_HEIGHT = 'EXPANDED_EDITOR_HEIGHT',
}

export interface ChildVisualizationViewerProps<TVisualizationType extends VisualizationType> {
  dataFrame: DataFrame;
  isLegacyInsights: boolean;
  parameters: VisualizationTypeToParameters[TVisualizationType];
}

type VisualizationViewerType<TVisualizationType extends VisualizationType> = React.FC<
  ChildVisualizationViewerProps<TVisualizationType>
>;

const VISUALIZATION_VIEWERS: {[k in VisualizationType]: VisualizationViewerType<k>} = {
  [VisualizationType.BAR_CHART]: BarChartViewer,
  [VisualizationType.HEATMAP]: HeatmapViewer,
  [VisualizationType.LINE_CHART]: LineChartViewer,
  [VisualizationType.PIE_CHART]: PieChartViewer,
  [VisualizationType.SCATTER_PLOT]: ScatterPlotViewer,
  [VisualizationType.TABLE]: TableViewer,
};

export interface VisualizationViewerProps {
  dataFrame: DataFrame;
  delayExpensiveRenders?: boolean;
  isLegacyInsights: boolean;
  viewerHeight: VisualizationHeight;
  visualization: AnalyticsVisualization;
  experimentalShapashTableOptions?: ExperimentalShapashTableOptions;
  regressionAnalyses?: Array<SuccessfulAnalysis<RegressionAnalysis>>;
}

// The stubbable functions have to be defined as part of some other object to be stubbable
export const _stubbableVisualizationViewerFunctions = {
  _stubbableVisualizationViewerBrowserRender: noop,
};

function VisualizationViewer({
  dataFrame,
  delayExpensiveRenders = false,
  isLegacyInsights,
  viewerHeight,
  visualization,
  experimentalShapashTableOptions,
  regressionAnalyses,
}: VisualizationViewerProps): JSX.Element | null {
  // We call a noop function in the render function of the component to be able to stub it. We can't directly
  // stub the functional component because it's memo'd, so instead we can stub a function that is called on
  // render. Using this noop gives us a consistent function to do that with. This is useful to test if a
  // render happened or not with certain actions (to make sure we're not breaking the React.memo)
  //
  // See "does not frivolously re-render the visualization viewer when the query is updated" in
  // client/insights/analytics-queries/block-editor/__tests__/AnalyticsBlockEditorContent-test.tsx for usage.
  _stubbableVisualizationViewerFunctions._stubbableVisualizationViewerBrowserRender();

  const visualizationType = getVisualizationType(visualization);

  // TODO(BNCH-19748): Test removing this hook call.
  const renderDelayComplete = useDelayedRender(!delayExpensiveRenders);
  // Only use useHasBeenInView on table visualizations, since tables are the most likely to cause render lag.
  // This has the desired side effect that block visualizations with regressions run on initial render and
  // don't wait until the visualization is scrolled into view.
  const {ref, hasBeenInView} = useHasBeenInView(visualizationType === VisualizationType.TABLE);

  const showVisualization = renderDelayComplete && (hasBeenInView ?? true);

  const VisualizationViewerComponent = VISUALIZATION_VIEWERS[visualizationType];

  return (
    // ChartJS visualizations are made to grow to fit the height of their parent container. We use maxHeight
    // as the height of the visualization editor, which works for tables, errors, and the loading throbber
    // because they have fixed heights. ChartJS charts on the other hand, don't know how to grow to the
    // maxHeight, so we set it to a set constant height before putting it in the visualization editor.
    <div
      className={classNames(
        'u-flex-full-height',
        css(
          VISUALIZATION_TYPE_TO_FLEX_GROW[visualizationType] && VISUALIZATION_HEIGHT_TO_STYLE[viewerHeight],
          !showVisualization && visualizationViewerStyles.centeredContent,
          VISUALIZATION_TYPE_TO_NEEDS_PADDING[visualizationType] && visualizationViewerStyles.contentMargin
        )
      )}
      ref={ref}
      {...getTestProps({component: 'VisualizationViewer', element: 'root'})}
    >
      {showVisualization ? (
        <VisualizationViewerComponent
          dataFrame={dataFrame}
          isLegacyInsights={isLegacyInsights}
          // TypeScript doesn't know that the correct AnalyticsVisualizationParameters type matches the
          // VisualizationType that gave us VisualizationViewerComponent.
          // @ts-expect-error
          parameters={visualization.parameters}
          experimentalShapashTableOptions={experimentalShapashTableOptions}
          regressionAnalyses={regressionAnalyses}
        />
      ) : (
        <CenteredHorizontalThrobber />
      )}
    </div>
  );
}

VisualizationViewer.defaultProps = {
  viewerHeight: VisualizationHeight.DYNAMIC,
};

export default React.memo(VisualizationViewer);

export const visualizationViewerStyles = StyleSheet.create({
  editorHeight: {
    height: VISUALIZATION_EDITOR_HEIGHT,
  },
  expandedEditorHeight: {
    height: EXPANDED_BLOCK_HEIGHT,
  },
  centeredContent: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  contentMargin: {
    margin: PANE_MARGIN,
  },
});

export const VISUALIZATION_HEIGHT_TO_STYLE: {
  [key in VisualizationHeight]: StyleDeclaration | undefined;
} = {
  [VisualizationHeight.DYNAMIC]: undefined,
  [VisualizationHeight.EDITOR_HEIGHT]: visualizationViewerStyles.editorHeight,
  [VisualizationHeight.EXPANDED_EDITOR_HEIGHT]: visualizationViewerStyles.expandedEditorHeight,
};
